import type { Order } from '@cracked-mug/shared';
import { Coffee, Hourglass, Timer, type LucideIcon } from 'lucide-react';

interface StatsBarProps {
  orders: Order[];
}

function isToday(iso: string, now: Date): boolean {
  const d = new Date(iso);
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export function StatsBar({ orders }: StatsBarProps) {
  const now = new Date();
  const ordersToday = orders.filter((o) => isToday(o.placedAt, now)).length;
  const activeOrders = orders.filter(
    (o) => o.status !== 'completed' && o.status !== 'cancelled',
  ).length;
  const completedWithTimes = orders.filter(
    (o) => o.status === 'completed' && o.acceptedAt !== null && o.readyAt !== null,
  );
  const avgPrepMs =
    completedWithTimes.length === 0
      ? 0
      : completedWithTimes.reduce((sum, o) => {
          const accepted = new Date(o.acceptedAt ?? o.placedAt).getTime();
          const ready = new Date(o.readyAt ?? o.completedAt ?? o.placedAt).getTime();
          return sum + (ready - accepted);
        }, 0) / completedWithTimes.length;
  const avgPrepMin = Math.round(avgPrepMs / 60_000);

  return (
    <div className="grid grid-cols-3 gap-4 px-6 py-4">
      <Stat icon={Coffee} label="Orders today" value={String(ordersToday)} />
      <Stat icon={Hourglass} label="In queue" value={String(activeOrders)} />
      <Stat icon={Timer} label="Avg prep" value={`${String(avgPrepMin)}m`} />
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-wca-black/[0.06] bg-wca-surface p-4 shadow-card transition-all duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-card-lg">
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono text-[11px] uppercase tracking-widest text-wca-gray-mid">{label}</p>
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-wca-gold/15 text-[#8a6d1f]">
          <Icon size={14} aria-hidden="true" />
        </span>
      </div>
      <p className="mt-2 font-mono text-3xl font-bold tabular-nums text-wca-black">{value}</p>
    </div>
  );
}
